import type { Brand } from 'backend';
import classNames from 'classnames/bind';
import { useTranslation } from 'next-i18next';
import { CSSProperties } from 'react';
import { Vid } from '../../elements';
import { useFonts } from '../../hooks';
import { Img } from '../Img';
import styles from './index.module.scss';

export type CoverProps = {
  className?: string;
  onClick?: (e: any) => void;
  href?: string;
  target?: string;
  brand: Brand;
  cover_settings_logo?: boolean | null;
  /** Legacy Directus field. Prefer cover_settings_logo for saved stories. */
  cover_logo_hide?: boolean | null;
  [key: string]: any;
};

export const Cover = ({
  id,
  title = '',
  brand,
  label,
  cover_image,
  cover_video,
  cover_layout = 'RESPONSIVE',
  cover_theme,
  cover_alignment = 'CENTER',
  cover_position = 'CENTER',
  cover_typeface = 'sans',
  cover_text_scale = 1,
  cover_text_color,
  cover_button_label,
  cover_button_color,
  cover_button_background,
  cover_overlay = '#0000000',
  cover_settings_logo,
  cover_logo_hide,
  cover_title_hide,
  cover_image_crop,
  href,
  target,
  onClick,
  ...rest
}: CoverProps) => {
  const { style, resources } = useFonts(brand ?? ({} as Brand), cover_typeface);
  const { t } = useTranslation('common');
  const imageStyle = {} as CSSProperties;
  const hideLogo = cover_settings_logo ?? cover_logo_hide ?? false;

  // @ts-ignore
  if (cover_image_crop?.position) {
    imageStyle.objectPosition = `${
      // @ts-ignore
      Math.abs(cover_image_crop.position.x ?? 0.5) * 100
      // @ts-ignore
    }% ${Math.abs(cover_image_crop.position.y ?? 0.5) * 100}%`;
  }

  const typeface_type = cover_typeface?.split(':')[0];

  // set classnames
  const cx = classNames.bind(styles)(
    'cover',
    `color-${cover_theme}`,
    `type-${
      typeface_type && ['sans', 'serif'].includes(typeface_type)
        ? typeface_type
        : 'custom'
    }`,
    `align-${cover_alignment?.toLowerCase()}`,
    `position-${cover_position?.toLowerCase()}`,
    `layout-${cover_layout?.toLowerCase()}`,
    { ...rest, hideLogo },
  );

  // render
  return (
    <>
      {resources}
      <a
        className={cx}
        href={href}
        target={target}
        style={style}
        onClick={onClick}
        key={id}
      >
        <div className={styles.inside}>
          <div className={styles.image}>
            {!!cover_image && (
              <Img
                className={styles.imageImg}
                src={cover_image}
                alt=""
                priority
                fill
                style={imageStyle}
              />
            )}

            {!cover_image && !cover_video && (
              <div
                className={styles.imageFill}
                style={{
                  backgroundColor:
                    brand?.color ?? (cover_theme == 'DARK' ? '#000' : '#ccc'),
                }}
              />
            )}

            {!!cover_video && (
              <Vid
                className={styles.image}
                src={cover_video}
                autoPlay
                loop
                muted
                playsInline
              />
            )}

            <div
              className={styles.overlay}
              style={{ backgroundColor: cover_overlay ?? undefined }}
            />
          </div>

          <div
            className={styles.text}
            style={{ color: cover_text_color ?? undefined }}
          >
            {(!brand?.logo_white || hideLogo) && (
              <span className={styles.textBrand}>{brand?.name}</span>
            )}
            {brand?.logo_white && !hideLogo && (
              <span className={styles.textLogo}>
                <Img
                  className={styles.textLogoImage}
                  src={brand.logo_white}
                  fill
                  priority
                  alt={brand?.name ?? ''}
                />
              </span>
            )}
            {!cover_title_hide && (
              <div className={styles.textTitle}>
                <div
                  className={styles.textTitleSizer}
                  style={{ fontSize: `${cover_text_scale ?? 1}em` }}
                >
                  {!!label && (
                    <em>
                      <span>{label}</span>
                    </em>
                  )}
                  <span>{(title ?? '').replace(/\\n/g, '\n')}</span>
                </div>
              </div>
            )}
            <span className={styles.textGo}>
              <span
                className={styles.button}
                style={{
                  color: cover_button_color ?? brand?.color ?? '#000',
                  backgroundColor: cover_button_background ?? undefined,
                }}
              >
                <span>
                  {cover_button_label ??
                    t('open-cover', { lng: brand?.language ?? undefined })}
                </span>
              </span>
            </span>
          </div>
        </div>
      </a>
    </>
  );
};
